import { useCallback, useState, useRef, useEffect } from "react";
import { User } from "../types/api";
import apiService from "../services/api";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const initedRef = useRef(false);
  const [state, setState] = useState<AuthState>(() => {
    // Check if token is in URL query params first
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");

    const token = urlToken || localStorage.getItem("token");

    // If token came from URL, store it in localStorage and clean up URL
    if (urlToken) {
      localStorage.setItem("token", urlToken);
      // Clean up the URL to remove the token parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return {
      user: null,
      token: token,
      isAuthenticated: !!token,
      isLoading: !!token, // Only load if we have a token to verify
      error: null,
    };
  });

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");
      if (token && !initedRef.current) {
        initedRef.current = true;
        console.log("[useAuth] Initializing auth with existing token");
        try {
          const user = await apiService.getAuthMe(token);
          console.log("[useAuth] Got user details from initialization");
          setState((prev) => ({
            ...prev,
            user: user,
            isLoading: false,
          }));
        } catch (error) {
          console.error("[useAuth] Failed to get user details on init", error);
          localStorage.removeItem("token");
          setState((prev) => ({
            ...prev,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          }));
        }
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    console.log("[useAuth] Login attempt started");
    setState((prev) => {
      console.log("[useAuth] Setting loading state");
      return { ...prev, isLoading: true, error: null };
    });
    try {
      const response = await apiService.login({
        email,
        password,
      });
      console.log("[useAuth] Login successful, storing token");
      const token = response.authToken;
      localStorage.setItem("token", token);

      // Get user details from auth_me endpoint
      console.log("[useAuth] Fetching user details from auth_me");
      const user = await apiService.getAuthMe(token);
      console.log("[useAuth] Got user details");

      const newState = {
        user: user,
        token: token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
      console.log("[useAuth] Setting new auth state:", newState);
      setState(newState);
    } catch (error: any) {
      // Check if this is a 2FA-related error that should be handled by LoginScreen
      // Don't modify ANY state for these errors - just re-throw immediately
      const is2FAError =
        error.response?.status === 401 &&
        (error.response?.data?.detail?.message === "2FA setup is required" ||
          error.response?.data?.detail?.message === "2FA code is required");

      if (is2FAError) {
        // The login request has finished, so clear the loading flag before
        // handing off to LoginScreen's 2FA modal. Otherwise isLoading stays
        // true and the login button (disabled={isLoading}) is stuck disabled
        // once the modal is dismissed - e.g. after a wrong 2FA code. Leave
        // `error` untouched: the 2FA modal surfaces its own message.
        console.log("[useAuth] 2FA error detected, clearing loading state and re-throwing for LoginScreen to handle");
        setState((prev) => ({ ...prev, isLoading: false }));
        throw error;
      }

      // For non-2FA errors, set the error message and loading state
      // Check if the response has a detail field (e.g., "Too many failed attempts")
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        "Login failed. Please try again.";
      console.log("[useAuth] Login failed, setting error:", errorMessage);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  const googleLoginInit = useCallback(async () => {
    try {
      // Determine redirect URI based on environment
      let redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI;

      if (!redirectUri) {
        // Use current origin for redirect
        redirectUri = window.location.origin + "/";
      }

      console.log("[useAuth] Initiating Google login with redirect URI:", redirectUri);
      const response = await apiService.googleLoginInit(redirectUri);

      // Redirect to the Google auth URL from the API
      if (response.authUrl) {
        window.location.href = response.authUrl;
      } else {
        throw new Error("No authUrl received from google_login_init");
      }
    } catch (error: any) {
      // FastAPI reports a failure in `detail`; only `error` was read here, so
      // every backend reason -- a timeout, a misconfigured OAuth app, Google
      // being down -- arrived as the same generic sentence and the real cause
      // never reached the screen or the person reporting it. Password login two
      // functions above already reads both.
      //
      // By the time this runs the request has already been tried three times
      // (see googleLoginInit), so "Please try again" on its own would be
      // advice the user has unknowingly taken twice. Say what is actually
      // wrong, and keep the password form as the way in either way.
      const noResponse = !error.response;
      const offline =
        typeof navigator !== "undefined" && navigator.onLine === false;
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        (offline
          ? "You appear to be offline. Check your connection and try again."
          : noResponse
          ? "Could not reach the server for Google sign-in. Try again in a moment, or sign in with your email and password."
          : "Google sign-in is unavailable right now. Try again in a moment, or sign in with your email and password.");
      console.error("[useAuth] Google login init failed:", {
        status: error.response?.status,
        data: error.response?.data,
        code: error.code,
        message: error.message,
        online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      });
      setState((prev) => ({
        ...prev,
        error: errorMessage,
      }));
    }
  }, []);

  const googleLogin = useCallback(async (code: string, redirectUri: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await apiService.googleLogin(code, redirectUri);
      localStorage.setItem("token", response.authToken);
      localStorage.setItem("user", JSON.stringify(response.user));
      setState({
        user: response.user,
        token: response.authToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        "Google login failed. Please try again.";
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  return {
    ...state,
    login,
    googleLoginInit,
    googleLogin,
    logout,
  };
};
